import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useTheme, useMediaQuery } from '@mui/material';

ChartJS.register(ArcElement, Tooltip, Legend);

interface PieChartProps {
  data: {
    labels: string[];
    datasets: {
      data: number[];
      backgroundColor: string[];
      borderColor: string[];
      borderWidth?: number;
    }[];
  };
  title?: string;
  height?: number;
}

export const PieChart: React.FC<PieChartProps> = ({ data, title, height = 400 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  
  const options: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000,
    },
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right' as const,
        labels: {
          usePointStyle: true,
          padding: isSmallMobile ? 8 : isMobile ? 12 : isLargeScreen ? 24 : 20,
          font: {
            size: isSmallMobile ? 9 : isMobile ? 11 : isLargeScreen ? 16 : 14,
            weight: 600,
            family: 'Roboto, Arial, sans-serif',
          },
          color: '#1B5E20',
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels && data.datasets.length) {
              const dataset: any = data.datasets[0];
              return data.labels.map((label: any, i) => {
                const value = dataset.data[i] as number;
                const total = (dataset.data as number[]).reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                const displayLabel = isSmallMobile && label.length > 12 
                  ? label.substring(0, 10) + '...' 
                  : label;
                return {
                  text: `${displayLabel} (${percentage}%)`,
                  fillStyle: dataset.backgroundColor?.[i] || '#000',
                  strokeStyle: dataset.borderColor?.[i] || '#000',
                  lineWidth: 2,
                  pointStyle: 'circle',
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        },
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: isSmallMobile ? 14 : isMobile ? 16 : isLargeScreen ? 20 : 18,
          weight: 'bold',
        },
        padding: {
          bottom: isMobile ? 15 : isLargeScreen ? 30 : 25,
        },
        color: '#26372D',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            })} (${percentage}%)`;
          }
        }
      }
    },
  };

  return (
    <div style={{ 
      height: `${height}px`, 
      position: 'relative',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <Pie data={data} options={options} />
    </div>
  );
};